/**
 * Add-on quantity control - shared by the account billing tab (web_account) and the
 * site-owner plan page (web_admin), so the two cannot drift apart.
 *
 * Two shapes, picked by the add-on's own ceiling (`maxQuantity` from
 * `GET /api/billing/add-ons`):
 *  - `max === 1` → an on/off switch. A switch-style add-on (e.g. priority support) only
 *    flips a boolean for the WHOLE account, so a second unit would change nothing except
 *    the invoice. A stepper would invite exactly that mistake.
 *  - `max > 1`  → a -/+ stepper, clamped to [0, max].
 *
 * The control only moves the pending value; buying/removing stays with the caller
 * (confirmation dialog + API call). The server enforces the same ceiling.
 */
import { Minus, Plus } from "lucide-react";
import { Switch } from "../components/switch";

export interface AddOnQuantityControlProps {
  /** Pending quantity (0 = not subscribed). */
  value: number;
  /** Highest quantity allowed for this add-on. 1 → switch. */
  max: number;
  disabled?: boolean;
  onChange: (next: number) => void;
  /** Accessible label for the switch shape (the add-on name). */
  switchLabel: string;
}

export function AddOnQuantityControl({ value, max, disabled, onChange, switchLabel }: AddOnQuantityControlProps) {
  if (max <= 1) {
    return (
      <Switch
        checked={value > 0}
        disabled={disabled}
        aria-label={switchLabel}
        onCheckedChange={(on) => onChange(on ? 1 : 0)}
      />
    );
  }

  return (
    <div className="flex items-center rounded-lg border border-border">
      <button
        type="button"
        aria-label="-"
        disabled={disabled || value <= 0}
        onClick={() => onChange(Math.max(0, value - 1))}
        className="flex size-8 items-center justify-center text-muted-foreground disabled:opacity-40"
      >
        <Minus className="size-3.5" />
      </button>
      <span className="w-8 text-center text-sm font-medium tabular-nums">{value}</span>
      <button
        type="button"
        aria-label="+"
        disabled={disabled || value >= max}
        onClick={() => onChange(Math.min(max, value + 1))}
        className="flex size-8 items-center justify-center text-muted-foreground disabled:opacity-40"
      >
        <Plus className="size-3.5" />
      </button>
    </div>
  );
}
